import { PartnerFetchInstrumentApiSet } from 'api/partner/instrument';
import {
  PartnerFetchInstrumentDefectReportsApiSet,
  usePartnerFetchInstrumentDefectReportsApi,
} from 'api/partner/instruments/defect_report';
import {
  PartnerFetchInstrumentProofreadEventsApiSet,
  usePartnerFetchInstrumentProofreadEventsApi,
} from 'api/partner/instruments/proofread_events';
import {
  PartnerFetchInstrumentRepairEstimatesApiSet,
  usePartnerFetchInstrumentRepairEstimatesApi,
} from 'api/partner/instruments/repair_estimates';
import {
  PartnerFetchInstrumentRepairEventsApiSet,
  usePartnerFetchInstrumentRepairEventsApi,
} from 'api/partner/instruments/repair_event';
import { ObjectType } from 'components/partner/instrument/instrument_event_table';
import { DefectReport } from 'entities/defect_report';
import { ProofreadEvent } from 'entities/proofread_event';
import { RepairEstimate } from 'entities/repair_estimate';
import { RepairEvent } from 'entities/repair_event';
import { useState } from 'react';
import { useEffectSkipFirst } from 'utils/hooks';

export type PartnerInstrumentEventTableSet = {
  defectReports: DefectReport[];
  repairEstimates: RepairEstimate[];
  repairEvents: RepairEvent[];
  proofreadEvents: ProofreadEvent[];
  defectReport?: DefectReport;
  setDefectReport: React.Dispatch<React.SetStateAction<DefectReport | undefined>>;
  repairEstimate?: RepairEstimate;
  setRepairEstimate: React.Dispatch<React.SetStateAction<RepairEstimate | undefined>>;
  repairEvent?: RepairEvent;
  setRepairEvent: React.Dispatch<React.SetStateAction<RepairEvent | undefined>>;
  proofreadEvent?: ProofreadEvent;
  fetchReportsApi: PartnerFetchInstrumentDefectReportsApiSet;
  fetchRepairEventsApi: PartnerFetchInstrumentRepairEventsApiSet;
  fetchRepairEstimatesApi: PartnerFetchInstrumentRepairEstimatesApiSet;
  fetchProofreadsApi: PartnerFetchInstrumentProofreadEventsApiSet;
  handleRefetchDefectReports: () => void;
  handleRefetchRepairEvents: () => void;
  handleRefetchRepair: () => void;
  handleSelectRow: (object: Object, type: ObjectType) => void;
};

export const usePartnerInstrumentEventTableSet = (
  instrumentShowApi: PartnerFetchInstrumentApiSet
): PartnerInstrumentEventTableSet => {
  const [isInitLoading, setIsInitLoading] = useState<boolean>(true);
  const [defectReports, setDefectReports] = useState<DefectReport[]>([]);
  const [repairEstimates, setRepairEstimates] = useState<RepairEstimate[]>([]);
  const [repairEvents, setRepairEvents] = useState<RepairEvent[]>([]);
  const [proofreadEvents, setProofreadEvents] = useState<ProofreadEvent[]>([]);

  const [defectReport, setDefectReport] = useState<DefectReport>();
  const [repairEstimate, setRepairEstimate] = useState<RepairEstimate>();
  const [repairEvent, setRepairEvent] = useState<RepairEvent>();
  const [proofreadEvent, setProofreadEvent] = useState<ProofreadEvent>();

  const instrumentId = instrumentShowApi.response.instrument.id;

  const fetchReportsApi = usePartnerFetchInstrumentDefectReportsApi(instrumentId);
  const fetchRepairEventsApi = usePartnerFetchInstrumentRepairEventsApi(instrumentId);
  const fetchRepairEstimatesApi = usePartnerFetchInstrumentRepairEstimatesApi(instrumentId);
  const fetchProofreadsApi = usePartnerFetchInstrumentProofreadEventsApi(instrumentId);

  const handleRefetchDefectReports = () => {
    setDefectReports([]);
    if (fetchReportsApi.pageSet.page > 1) {
      fetchReportsApi.pageSet.setPage(1);
    } else {
      fetchReportsApi.execute();
    }
  };

  const handleRefetchRepairEvents = () => {
    setRepairEvents([]);
    if (fetchRepairEventsApi.pageSet.page > 1) {
      fetchRepairEventsApi.pageSet.setPage(1);
    } else {
      fetchRepairEventsApi.execute();
    }
  };

  const handleRefetchRepair = () => {
    setDefectReports([]);
    setRepairEvents([]);
    setRepairEstimates([]);
    if (fetchReportsApi.pageSet.page > 1) {
      fetchReportsApi.pageSet.setPage(1);
    } else {
      fetchReportsApi.execute();
    }
    if (fetchRepairEstimatesApi.pageSet.page > 1) {
      fetchRepairEstimatesApi.pageSet.setPage(1);
    } else {
      fetchRepairEstimatesApi.execute();
    }
    if (fetchRepairEventsApi.pageSet.page > 1) {
      fetchRepairEventsApi.pageSet.setPage(1);
    } else {
      fetchRepairEventsApi.execute();
    }
  };

  const handleSelectRow = (object: DefectReport | RepairEstimate | RepairEvent | ProofreadEvent, type: ObjectType) => {
    switch (type) {
      case 'DefectReport': {
        const report = object as DefectReport;
        setDefectReport(report);
        setRepairEstimate(report?.repairEstimate);
        setRepairEvent(report?.repairEvent);
        setProofreadEvent(report?.proofreadEvent);
        break;
      }
      case 'RepairEstimate': {
        const rEstimate = object as RepairEstimate;
        setDefectReport(rEstimate?.defectReport);
        setRepairEstimate(rEstimate);
        setRepairEvent(rEstimate.repairEvent);
        setProofreadEvent(rEstimate?.defectReport?.proofreadEvent);
        break;
      }
      case 'RepairEvent': {
        const rEvent = object as RepairEvent;
        setDefectReport(rEvent?.defectReport);
        setRepairEstimate(rEvent?.repairEstimate);
        setRepairEvent(rEvent);
        setProofreadEvent(rEvent?.proofreadEvent);
        break;
      }
      case 'ProofreadEvent': {
        const pEvent = object as ProofreadEvent;
        setDefectReport(pEvent?.defectReport);
        setRepairEstimate(pEvent?.defectReport?.repairEstimate);
        setRepairEvent(pEvent?.repairEvent);
        setProofreadEvent(pEvent);
        break;
      }
      default:
        break;
    }
  };

  useEffectSkipFirst(() => {
    if (!instrumentShowApi.isSuccess()) return;
    fetchReportsApi.pageSet.setPerPage(3);
    fetchRepairEstimatesApi.pageSet.setPerPage(3);
    fetchRepairEventsApi.pageSet.setPerPage(3);
    if (instrumentShowApi.response.instrument.proofreadInfo) {
      fetchProofreadsApi.pageSet.setPerPage(3);
    }
  }, [instrumentShowApi.loading]);

  useEffectSkipFirst(() => {
    if (!isInitLoading) return;
    // 最初の画面読み込み時に一番最新の不具合または校正履歴を表示する
    if (fetchReportsApi.isSuccess()) {
      handleSelectRow(fetchReportsApi.response.defectReports[0], 'DefectReport');
      setIsInitLoading(false);
    } else if (fetchProofreadsApi.isSuccess()) {
      handleSelectRow(fetchProofreadsApi.response.proofreadEvents[0], 'ProofreadEvent');
      setIsInitLoading(false);
    }
  }, [fetchReportsApi.loading, fetchProofreadsApi.loading]);

  useEffectSkipFirst(() => {
    if (!fetchReportsApi.isSuccess()) return;
    setDefectReports([...defectReports, ...fetchReportsApi.response.defectReports]);
  }, [fetchReportsApi.loading]);

  useEffectSkipFirst(() => {
    if (!fetchRepairEstimatesApi.isSuccess()) return;
    setRepairEstimates([...repairEstimates, ...fetchRepairEstimatesApi.response.repairEstimates]);
  }, [fetchRepairEstimatesApi.loading]);

  useEffectSkipFirst(() => {
    if (!fetchRepairEventsApi.isSuccess()) return;
    setRepairEvents([...repairEvents, ...fetchRepairEventsApi.response.repairEvents]);
  }, [fetchRepairEventsApi.loading]);

  useEffectSkipFirst(() => {
    if (!fetchProofreadsApi.isSuccess()) return;
    setProofreadEvents([...proofreadEvents, ...fetchProofreadsApi.response.proofreadEvents]);
  }, [fetchProofreadsApi.loading]);

  return {
    defectReports,
    repairEvents,
    repairEstimates,
    proofreadEvents,
    defectReport,
    setDefectReport,
    repairEstimate,
    setRepairEstimate,
    repairEvent,
    setRepairEvent,
    proofreadEvent,
    fetchReportsApi,
    fetchRepairEventsApi,
    fetchRepairEstimatesApi,
    fetchProofreadsApi,
    handleRefetchDefectReports,
    handleRefetchRepairEvents,
    handleRefetchRepair,
    handleSelectRow,
  };
};
